"use client"

import type { FormEvent } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { TextArea } from "@/components/ui/textarea"

import { type ComposerPreview, UnfurlPreview } from "./unfurl-preview"

export interface SuggestionDraft {
  url: string
  notes: string
  name: string
  email: string
  // Editable override, seeded from the unfurl. Unfurls are often wrong, so
  // the contributor gets the last word; the server persists this when set.
  title: string
}

interface ComposerFormProps {
  draft: SuggestionDraft
  errors: Partial<Record<string, string>>
  processing: boolean
  requiresGuestIdentity: boolean
  onFieldChange: <K extends keyof SuggestionDraft>(
    field: K,
    value: SuggestionDraft[K],
  ) => void
  onSubmit: (event: FormEvent) => void
  /** id of the card's question node, so the form declares which prompt it answers. */
  describedById: string
  // Unfurl state, owned by the composer and threaded down.
  loading: boolean
  failed: boolean
  preview: ComposerPreview | null
  reduceMotion: boolean
}

// A quiet entrance for each newly-earned field. Cheap (opacity + a short
// slide), and silenced under reduced motion.
const REVEAL =
  "animate-in fade-in slide-in-from-top-1 duration-200 ease-out motion-reduce:animate-none"

// The answering half of the composer, unfurling as the contributor goes:
// just a link → (skeleton preview + the "why") → editable details → sign it.
// Each ask appears only once it's earned. One primary button on the whole
// page, and it's here.
export function ComposerForm({
  draft,
  errors,
  processing,
  requiresGuestIdentity,
  onFieldChange,
  onSubmit,
  describedById,
  loading,
  failed,
  preview,
  reduceMotion,
}: ComposerFormProps) {
  // The stale-card case (`track_prompt`) and pre-validation guards
  // (`base`) have no field to hang on — they get the alert.
  const blockingError = errors.base ?? errors.track_prompt

  const hasUrl = draft.url.trim() !== ""
  // The unfurl round-trip has produced something to show (a result or a
  // definitive failure) — the beat after which the identity ask is earned.
  const settled = preview != null || failed
  const showIdentity = requiresGuestIdentity && hasUrl && settled

  // Announce the async unfurl outcome for assistive tech, since it changes
  // what's on screen without a focus move.
  const liveMessage = !hasUrl
    ? ""
    : loading && !settled
      ? "Looking up this link…"
      : failed ||
          (preview && preview.status !== "ok" && preview.status !== "partial")
        ? "We couldn't fetch the link details — you can add a title yourself."
        : preview
          ? "Link details loaded."
          : ""

  return (
    <form
      onSubmit={onSubmit}
      aria-describedby={describedById}
      className="flex flex-col gap-4"
    >
      <Input
        label="Your answer"
        placeholder="Paste a link — Spotify, YouTube, an article, anything that fits"
        value={draft.url}
        onChange={(value) => onFieldChange("url", value)}
        isRequired
        hint={errors.url}
        isInvalid={!!errors.url}
      />

      <p className="sr-only" role="status" aria-live="polite">
        {liveMessage}
      </p>

      {hasUrl && (
        <div className={REVEAL}>
          <UnfurlPreview
            loading={loading}
            failed={failed}
            preview={preview}
            title={draft.title}
            onTitleChange={(value) => onFieldChange("title", value)}
            titleError={errors.title}
            reduceMotion={reduceMotion}
          />
        </div>
      )}

      {hasUrl && (
        <div className={REVEAL}>
          <TextArea
            label="Why does this fit?"
            rows={3}
            placeholder="One or two lines is plenty"
            value={draft.notes}
            onChange={(value) => onFieldChange("notes", value)}
            hint={errors.notes}
            isInvalid={!!errors.notes}
          />
        </div>
      )}

      {showIdentity && (
        <div
          className={`border-batyr-ink/10 flex flex-col gap-3 border-t pt-4 ${REVEAL}`}
        >
          <p className="text-tertiary text-xs">
            So we can credit you (and tell you if it&apos;s picked)
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="flex-1">
              <Input
                label="Your name"
                placeholder="How should we credit this?"
                value={draft.name}
                onChange={(value) => onFieldChange("name", value)}
                isRequired
                hint={errors.name}
                isInvalid={!!errors.name}
              />
            </div>
            <div className="flex-1">
              <Input
                label="Email"
                type="email"
                placeholder="So we can let you know if it gets picked"
                value={draft.email}
                onChange={(value) => onFieldChange("email", value)}
                isRequired
                hint={errors.email}
                isInvalid={!!errors.email}
              />
            </div>
          </div>
        </div>
      )}

      {blockingError && (
        <div
          role="alert"
          className="border-error bg-error-primary text-error-primary rounded-lg border px-4 py-3 text-sm"
        >
          {blockingError}
        </div>
      )}

      <div className="flex justify-end pt-1">
        <Button type="submit" color="primary" size="md" isDisabled={processing}>
          Add my answer
        </Button>
      </div>
    </form>
  )
}
